import type { NameSlots } from "./session-name";

// ─── Primitives ─────────────────────────────────────────────────────

export type MessageSender = "user" | "assistant";
export type Include = "all" | "conversations" | "subagents" | "teammates";
export type View = "flat" | "tree" | "grouped";
export type SortOrder = "recent" | "oldest" | "messages-desc" | "messages-asc" | "alpha";

export const VALID_SORT_ORDERS: SortOrder[] = [
  "recent",
  "oldest",
  "messages-desc",
  "messages-asc",
  "alpha",
];

// ─── Profile ────────────────────────────────────────────────────────

export interface Profile {
  id: string;
  label: string;
  configDir: string;
  enabled: boolean;
  emoji?: string;
  scanHistory?: boolean;
}

// ─── Content Tiers ──────────────────────────────────────────────────

export interface ContentTier {
  name: string;
  previewMax: number;
  snippetMax: number;
}

// ─── ConversationMeta (full superset) ───────────────────────────────

export interface MessageSnapshot {
  text: string;
  timestamp: string;
}

export type ProviderName = "claude-code" | "codex-cli" | "cursor" | "copilot";

export interface ConversationMeta {
  id: string;
  filePath: string;
  sessionId: string;
  // Which provider produced this meta. Optional/additive: existing claude-code
  // metas default to "claude-code" when unset.
  provider?: ProviderName;
  // Distinguishes plain chat conversations from task-oriented logs (Codex).
  kind?: "conversation" | "task";
  // Provider-native session id when it differs from the (non-unique) sessionId.
  externalSessionId?: string;
  sessionName: string;
  // The slots sessionName was resolved from (resolveSessionName(nameSlots).name),
  // so a host can merge further candidates through applyNameCandidate.
  nameSlots?: NameSlots;
  projectPath: string;
  projectName: string;
  account: string;
  timestamp: string;
  messageCount: number;
  lastMessageSender: MessageSender;
  preview: string;
  contentSnippet: string;
  gitBranch: string | null;
  model: string | null;
  isSubagent: boolean;
  // NOTE: despite the name, this holds the parent's JSONL FILE PATH, not an id
  // — it is matched against `id` (also a file path) by the "tree" view. For the
  // parent's actual session UUID use `parentSessionUuid` below.
  parentSessionId: string | null;
  // The subagent's OWN identity, distinct from `sessionId`: a Claude sidechain
  // transcript carries its PARENT's id in the `sessionId` field, so `sessionId`
  // alone collapses every sibling subagent (and the parent) onto one identity.
  // Claude's `agentId`; undefined for Codex, which exposes no per-child id.
  subagentId?: string;
  // The parent conversation's session UUID (Claude: the sidechain line's
  // `sessionId`; Codex: `thread_spawn.parent_thread_id`). Undefined on ordinary
  // conversations and on subagents whose parent is not discoverable.
  parentSessionUuid?: string;
  isTeammate: boolean;
  teamName: string | null;
  toolNames: string[];
  firstMessage: MessageSnapshot | null;
  lastMessage: MessageSnapshot | null;
  lastPrompt?: string;
  // Set when a Cursor agent-transcripts file is a copy of another provider's
  // session. isImportedFromCursor is reserved for Claude/Codex providers.
  isImportedFromClaude?: boolean;
  isImportedFromCodex?: boolean;
  isImportedFromCursor?: boolean;
}

// ─── View Variants ──────────────────────────────────────────────────

export interface TreeConversation extends ConversationMeta {
  subagents: ConversationMeta[];
}

export interface GroupedConversations {
  [groupKey: string]: ConversationMeta[];
}

// ─── Options ────────────────────────────────────────────────────────

export interface FileStatEntry {
  mtimeMs: number;
  size: number;
}

export interface ScanOptions {
  profiles?: Profile[];
  // Providers to scan. Defaults to ["claude-code"]. Including "codex-cli"
  // requires codexRoots; including "cursor" requires cursorRoots; including
  // "copilot" requires copilotRoots. None glob $HOME by default. The live-PTY alias "cursor-cli" is accepted and
  // stored as "cursor".
  providers?: Array<ProviderName | "cursor-cli">;
  // Absolute roots to discover Codex CLI history under (e.g. ~/.codex/sessions).
  codexRoots?: string[];
  // Absolute roots to discover Cursor agent-transcripts under
  // (e.g. ~/.cursor/projects). Composer state.vscdb is not indexed, only
  // read for names (see cursorStateDb).
  cursorRoots?: string[];
  // Absolute path of Cursor's IDE `state.vscdb` (its `composerData:<id>` rows name
  // IDE chats). Opened read-only, one key lookup per chat; never read unless set.
  // Process-wide once set, like the chat transcripts it names.
  cursorStateDb?: string;
  // Absolute roots to discover GitHub Copilot CLI sessions under
  // (e.g. ~/.copilot/session-state). session-store.db is not read.
  copilotRoots?: string[];
  tier?: string;
  tiers?: Record<string, ContentTier>;
  include?: Include;
  view?: View;
  sort?: SortOrder;
  since?: string;
  project?: string;
  account?: string;
  limit?: number;
  offset?: number;
  onProgress?: (scanned: number, total: number) => void;
  onBatch?: (metas: ConversationMeta[]) => void;
  /** Known file stats from a previous scan. Files whose (mtimeMs, size) match
   *  are skipped — the cached ConversationMeta is reused instead. */
  statCache?: Map<string, { stat: FileStatEntry; meta: ConversationMeta }>;
  /** Persistent mode only: bypass the dir-mtime discovery gate and force a
   *  full glob of every profile's projectsDir, ignoring stored watermarks.
   *  Use for an explicit user-triggered refresh that shouldn't trust the
   *  gate's optimization. */
  fullRescan?: boolean;
}

export interface ScanResult {
  conversations: ConversationMeta[] | TreeConversation[] | GroupedConversations;
  total: number;
  scanned: number;
}

export interface SearchOptions extends ScanOptions {
  fields?: string[];
  // Restrict results to one provider. Accepts the live-PTY alias "cursor-cli".
  provider?: string;
}

// Half-open [start, end) character range into a SearchMatch.snippet marking one
// matched term, so a client can emphasise it without parsing markup. Ranges
// follow FTS token boundaries, not the query string: a prefix search for
// "timeout" highlights the whole token "timeoutMs".
export interface SearchHighlight {
  start: number;
  end: number;
}

export interface SearchMatch {
  field: string;
  snippet: string;
  // Present on body hits (field === "content"), where the excerpt comes from
  // FTS5 snippet(). Absent on metadata hits from generateMatches().
  highlights?: SearchHighlight[];
}

export interface SearchResult {
  meta: ConversationMeta;
  score: number;
  matches: SearchMatch[];
}

export interface GetConversationOptions {
  profiles?: Profile[];
}

export interface GetConversationPageOptions {
  beforeIndex?: number;
  limit: number;
}

export interface ConversationPage {
  messages: ConversationMessage[];
  total: number;
  fromIndex: number;
}

// A page sliced directly from a single parsed JSONL file, carrying the parsed
// Conversation alongside the window so a caller can build a full response
// (meta + messages) from one parse, without a prior scan().
export interface SingleFilePage extends ConversationPage {
  conversation: Conversation;
}

// ─── Full Conversation ──────────────────────────────────────────────

export interface TurnDuration {
  durationMs: number;
  messageCount: number;
  uuid?: string;
}

export interface DeferredToolsDeltaAttachment {
  type: "deferred_tools_delta";
  addedNames: string[];
  addedLines: unknown[];
  removedNames: string[];
}

export type AttachmentSidecar =
  | DeferredToolsDeltaAttachment
  | { type: string; [key: string]: unknown };

export interface ToolUseBlock {
  id: string;
  name: string;
  input: Record<string, unknown>;
}

export interface ToolResultBlock {
  toolUseId: string;
  type:
    | "edit"
    | "write"
    | "read"
    | "bash"
    | "grep"
    | "glob"
    | "taskAgent"
    | "taskCreate"
    | "taskUpdate"
    | "generic";
  content: Record<string, unknown>;
  isError?: boolean;
}

export interface TeamInfo {
  teammateId: string;
  summary?: string;
  color?: string;
}

export interface MessageMetadata {
  model?: string;
  stopReason?: string | null;
  inputTokens?: number;
  outputTokens?: number;
  cacheReadTokens?: number;
  cacheCreationTokens?: number;
  gitBranch?: string;
  version?: string;
  toolUses?: string[];
  toolUseBlocks?: ToolUseBlock[];
  toolResults?: ToolResultBlock[];
  teamName?: string;
  teamInfo?: TeamInfo;
}

export interface ConversationMessage {
  role: MessageSender;
  text: string;
  timestamp: string;
  uuid?: string;
  metadata?: MessageMetadata;
  isToolResult?: boolean;
  isThinking?: boolean;
  thinkingContent?: string;
  thinkingSignature?: string;
  parentUuid?: string | null;
  requestId?: string;
  promptId?: string;
  isSidechain?: boolean;
  permissionMode?: string;
  hasImages?: boolean;
  attachment?: AttachmentSidecar;
  // Claude Code's generated compaction summary ("This session is being
  // continued…"), written as a user row the user never typed. Kept in the
  // message list so message indexes don't move; consumers render it apart.
  isCompactSummary?: boolean;
  // The compact_boundary row that precedes the summary.
  compaction?: CompactionInfo;
}

export interface CompactionInfo {
  trigger?: string;
  preTokens?: number;
  postTokens?: number;
}

export interface Conversation {
  id: string;
  filePath: string;
  projectPath: string;
  projectName: string;
  sessionId: string;
  sessionName: string;
  messages: ConversationMessage[];
  fullText: string;
  timestamp: string;
  messageCount: number;
  account: string;
  turnDurations?: TurnDuration[];
  lastPrompt?: string;
}
